import { recordGameExit } from '../debug/exitBreadcrumbs';
import type { ConfirmOptions, ConfirmResult } from '../context/ConfirmContext';
import type { GameMode } from '../types/game';
import { t } from '../i18n';

/**
 * leaveDecision.ts — pure, dependency-injected leave-game state machine.
 *
 * No React, no router, no DOM: every side effect (confirmations, backend calls,
 * navigation proceed/stay) is injected, so the full decision matrix is unit-
 * testable in the node environment. `useLeaveGameGuard` is a thin adapter that
 * wires the real dependencies (ConfirmProvider, apiClient, react-router's
 * `useBlocker`) into `createLeaveController()` / `runLeaveDecision()`.
 */

export interface LeaveState {
  /** Authoritative backend game_mode. `undefined` (still loading / fetch
   *  failed) FAILS CLOSED: no destructive action is offered until the real
   *  mode is known — see the unknown-mode branch in runLeaveDecision. */
  mode: GameMode | undefined;
  sessionId: number;
  gameId: number;
}

export interface LeaveDeps {
  confirmChoice: (options: ConfirmOptions) => Promise<ConfirmResult>;
  confirm:       (options: ConfirmOptions) => Promise<boolean>;
  /** Destructive-only backend calls — "Leave and save" calls NEITHER. */
  closeSoloGame: (sessionId: number) => Promise<unknown>;
  leaveSession:  (sessionId: number) => Promise<unknown>;
  recordExit:    typeof recordGameExit;
  emitActiveGameChanged: () => void;
  /** Resume the ORIGINAL blocked navigation (react-router blocker.proceed()). */
  proceed: () => void;
  /** Cancel the navigation; stay on the game screen (blocker.reset()). */
  stay: () => void;
  /** Re-fetch the authoritative game_mode (unknown-mode retry path). */
  resolveMode?: () => Promise<GameMode | undefined>;
  /** Error sink; defaults to console.error. */
  onError?: (message: string, err: unknown) => void;
}

/**
 * Decide and enact a leave attempt. Never navigates itself — it only calls
 * `proceed()` (resume the blocked destination) or `stay()` (cancel), so the
 * router owns the destination and it cannot loop.
 *
 * One canonical "Leave this game?" modal per mode (approved design):
 *   Solo (bots), 3 actions: Stay at the table / Save for later (no backend —
 *     the solo game persists server-side) / Leave & end game (closeSoloGame).
 *   Online, 2 actions: Stay at the table / Leave & forfeit
 *     (leaveSession — records a loss and a forfeit; no rating system exists).
 *   UNKNOWN mode (still loading / fetch failed) FAILS CLOSED: no destructive
 *     action is shown or callable — a safe Stay/Try-again modal re-fetches the
 *     authoritative mode and only a confirmed 'bots'/'online' answer unlocks
 *     the real variant. An unknown mode can never forfeit or close a game.
 *
 * Destructive backend calls run INSIDE the modal (`*Run`): the selected button
 * shows a pending label, dismissal is blocked, and a failure keeps the modal
 * open with the inline error — the promise resolves with that action only
 * after the call has succeeded, so proceed() strictly follows success and a
 * failure can never navigate.
 */
export async function runLeaveDecision(state: LeaveState, deps: LeaveDeps): Promise<void> {
  const { mode, sessionId: sid, gameId: gid } = state;
  const logError = deps.onError ?? ((m: string, e: unknown) => console.error(m, e));

  // ── FAIL CLOSED on unknown mode ───────────────────────────────────────────
  // The modal variant may only come from the authoritative game_mode. With no
  // confirmed mode there is NO destructive action: the player can stay, or
  // retry the mode fetch inside the modal (pending + inline error on failure).
  // Only a confirmed 'bots'/'online' answer re-enters the real decision.
  if (mode !== 'bots' && mode !== 'online') {
    let resolvedMode: GameMode | undefined;
    // Copy resolves through the shared t() at decision time, so the dialog
    // opens in the active language. English values are character-identical.
    const choice = await deps.confirmChoice({
      title:         t('gameplay:leave.title'),
      message:       t('gameplay:leave.unknownBody'),
      cancelLabel:   t('gameplay:leave.stay'),
      cancelVariant: 'gold',
      confirmLabel:  t('gameplay:leave.tryAgain'),
      confirmVariant: 'neutral',
      confirmPendingLabel: t('gameplay:leave.checking'),
      errorText:     t('gameplay:leave.unknownError'),
      confirmRun: async () => {
        const m = await deps.resolveMode?.();
        if (m !== 'bots' && m !== 'online') {
          throw new Error('game mode still unknown');
        }
        resolvedMode = m;
      },
    });
    if (choice !== 'confirm' || resolvedMode === undefined) { deps.stay(); return; }
    // Mode is now authoritative — run the real decision for it.
    return runLeaveDecision({ ...state, mode: resolvedMode }, deps);
  }

  // Solo bot game (persistent): stay / save for later / permanent end.
  if (mode === 'bots') {
    const choice = await deps.confirmChoice({
      title:        t('gameplay:leave.title'),
      message:      t('gameplay:leave.botsBody'),
      cancelLabel:  t('gameplay:leave.stay'),
      cancelVariant: 'gold',
      confirmLabel: t('gameplay:leave.saveForLater'),
      confirmVariant: 'green',
      tertiaryLabel:   t('gameplay:leave.endGame'),
      tertiaryVariant: 'red',
      tertiaryPendingLabel: t('gameplay:leave.endingGame'),
      // Permanent close runs inside the modal; 'tertiary' resolves only after
      // it succeeded. Failure → modal stays open, nothing navigates.
      tertiaryRun: async () => {
        try {
          if (sid > 0) await deps.closeSoloGame(sid);
        } catch (err) {
          logError('[LeaveGuard] closeSoloGame failed — staying on game:', err);
          throw err;
        }
      },
    });

    if (choice === 'cancel') { deps.stay(); return; }       // Stay at the table

    if (choice === 'confirm') {                              // Save for later
      // Game stays active — NO closeSoloGame / leaveSession / forfeit.
      deps.recordExit('leave_and_save_bot', { gameId: gid, sessionId: sid, note: 'solo-pause-save' });
      deps.emitActiveGameChanged();
      deps.proceed();
      return;
    }

    // tertiary → Leave & end game: closeSoloGame already succeeded in-modal.
    deps.recordExit('end_solo_bot_game', { gameId: gid, sessionId: sid, note: 'solo-permanent-close' });
    deps.proceed();                                          // only after success
    return;
  }

  // Online multiplayer (mode === 'online', authoritative): forfeit.
  // Copy states current authoritative behavior only: forfeiting records a loss
  // (and a forfeit count). No rating system exists — do not claim one.
  const ok = await deps.confirm({
    title:        t('gameplay:leave.title'),
    message:      t('gameplay:leave.onlineBody'),
    cancelLabel:  t('gameplay:leave.stay'),
    cancelVariant: 'gold',
    confirmLabel: t('gameplay:leave.forfeit'),
    variant:      'danger',
    confirmPendingLabel: t('gameplay:leave.leaving'),
    confirmRun: async () => {
      try {
        if (sid > 0) await deps.leaveSession(sid);
      } catch (err) {
        logError('[LeaveGuard] leaveSession failed — staying on game:', err);
        throw err;
      }
    },
  });
  if (!ok) { deps.stay(); return; }                          // Stay at the table

  // leaveSession already succeeded in-modal.
  deps.recordExit('forfeit_match', { gameId: gid, sessionId: sid, note: 'multiplayer-forfeit' });
  deps.emitActiveGameChanged();
  deps.proceed();                                            // only after success
}

/**
 * Re-entrancy wrapper: at most one decision in flight. A second blocked-state
 * effect (StrictMode double-invoke, re-render, or another nav attempt while a
 * modal is open) is ignored until the first resolves — no overlapping modals
 * and proceed/stay fires at most once per decision.
 */
export function createLeaveController() {
  let deciding = false;
  return {
    isDeciding(): boolean { return deciding; },
    async handleBlocked(state: LeaveState, deps: LeaveDeps): Promise<void> {
      if (deciding) return;
      deciding = true;
      try { await runLeaveDecision(state, deps); }
      finally { deciding = false; }
    },
  };
}

/** True when a path is a game route ("/game/:id"). */
export function isGameRoute(path: string): boolean {
  return /^\/game\/[^/]+$/.test(path);
}

/**
 * Blocker predicate: block only a real path change that LEAVES gameplay.
 *
 * A change to another game route ("/game/A" → "/game/B") is NOT a leave — it is
 * the same session's next-round transition, whose URL is synced by GameScreen
 * after Phaser has already advanced internally (F6). Blocking it would pop the
 * forfeit-confirm modal on every round. Leaving to a NON-game route (/home,
 * /friends, …) is still blocked so the forfeit/leave decision runs.
 *
 * `matchCompleted` is the AUTHORITATIVE completed-match signal
 * (roundEndPayload.sessionOver from the backend's game_finished payload).
 * Once the whole match is over there is nothing to pause, save, or forfeit —
 * the rematch decision flow owns every exit, and its resolution navigation
 * (/play/online, /play/bots, legacy /home) must never be intercepted by the
 * active-game modal. Ordinary between-round Round End reports
 * sessionOver=false, so mid-session protection is unchanged; an accepted
 * rematch clears the payload, re-arming the guard for the successor match.
 *
 * `gamePath` is retained for API compatibility; the game-route check subsumes the
 * old "same page" allowance (staying on /game/X is a game route too).
 */
export function shouldBlockLeave(
  currentPath: string,
  nextPath: string,
  _gamePath: string,
  matchCompleted = false,
): boolean {
  if (matchCompleted) return false;
  return currentPath !== nextPath && !isGameRoute(nextPath);
}
